import { useState, useEffect, useCallback } from 'react';
import { AlertCircle, Ban, Check, Landmark, PlugZap, Save, Settings } from 'lucide-react';
import type { Account, BtgBoletoConfig } from '../types/financeV2.types';
import { financeV2Service } from '../services/financeV2Service';
import { getErrorMessage } from '../utils/error.utils'
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea } from '@/components/ui/textarea';
import { DetailSection } from '@/components/common/DetailSection';
import { FormActions, FormField, FormSection } from '@/components/common/FormSection';

interface BtgCredentialsForm {
  documentType: 'CPF' | 'CNPJ';
  documentNumber: string;
  companyName?: string;
  apiKey: string;
  clientId: string;
  clientSecret: string;
}

type BoletoConfig = Required<BtgBoletoConfig>;

export function FinanceV2BtgConfigPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCredentialsForm, setShowCredentialsForm] = useState(false);
  const [config, setConfig] = useState<BoletoConfig | null>(null);

  const [credentials, setCredentials] = useState<BtgCredentialsForm>({
    documentType: 'CNPJ',
    documentNumber: '',
    companyName: '',
    apiKey: '',
    clientId: '',
    clientSecret: ''
  });

  const loadAccounts = async () => {
    try {
      const response = await financeV2Service.accounts.list({ isActive: true });
      setAccounts(response.data.accounts);
    } catch (err: unknown) {
      setError('Erro ao carregar contas');
    }
  };

  const loadBoletoConfig = useCallback(async () => {
    if (!selectedAccount) return;

    try {
      const response = await financeV2Service.btg.getBoletoConfig(selectedAccount);
      setConfig(response.data!.config as BoletoConfig);
    } catch (err: unknown) {
      // Config doesn't exist yet, that's okay
      setConfig({
        defaultFinePercentage: 2,
        defaultInterestPercentage: 1,
        defaultDaysToDue: 30,
        defaultInstructions: '',
        sendEmailOnIssue: true,
        sendWhatsappOnIssue: false
      });
    }
  }, [selectedAccount]);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      loadBoletoConfig();
    }
  }, [selectedAccount, loadBoletoConfig]);

  const handleEnableBtg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount) {
      setError('Selecione uma conta');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await financeV2Service.btg.enableIntegration(selectedAccount, credentials);
      setSuccess('Integração BTG habilitada com sucesso!');
      setShowCredentialsForm(false);
      loadAccounts();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao habilitar BTG'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisableBtg = async () => {
    if (!selectedAccount || !confirm('Deseja realmente desabilitar a integração BTG?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await financeV2Service.btg.disableIntegration(selectedAccount);
      setSuccess('Integração BTG desabilitada');
      loadAccounts();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao desabilitar BTG'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAccount || !config) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await financeV2Service.btg.updateBoletoConfig(selectedAccount, config);
      setSuccess('Configuração atualizada com sucesso!');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao atualizar configuração'));
    } finally {
      setLoading(false);
    }
  };

  const selectedAccountData = accounts.find(a => a.id === selectedAccount);

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <Landmark className="size-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Configuração BTG Pactual</h1>
        </div>
        <p className="text-sm text-muted-foreground">Gerencie a integração com BTG para emissão de boletos</p>
      </div>

      <Card>
        <CardContent>
          <FormField label="Selecione a Conta Bancária" htmlFor="btg-account">
            <NativeSelect id="btg-account" value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}>
              <option value="">Selecione uma conta...</option>
              {accounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name} {account.isBtgEnabled && '✓ BTG Habilitado'}
                </option>
              ))}
            </NativeSelect>
          </FormField>
        </CardContent>
      </Card>

      {error && (
        <p role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="size-4 shrink-0 translate-y-0.5" aria-hidden="true" />
          {error}
        </p>
      )}

      {success && (
        <p role="status" className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm">
          <Check className="size-4 shrink-0 translate-y-0.5 text-primary" aria-hidden="true" />
          {success}
        </p>
      )}

      {selectedAccountData && (
        <>
          <DetailSection title="Status da Integração" icon={PlugZap}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-2">
                <Badge variant={selectedAccountData.isBtgEnabled ? 'default' : 'secondary'} className="self-start">
                  {selectedAccountData.isBtgEnabled ? 'Integração Ativa' : 'Integração Inativa'}
                </Badge>
                {selectedAccountData.isBtgEnabled && (
                  <p className="text-sm text-muted-foreground">Conta habilitada para emissão de boletos via BTG Pactual</p>
                )}
              </div>
              {!selectedAccountData.isBtgEnabled ? (
                <Button onClick={() => setShowCredentialsForm(true)}>Habilitar BTG</Button>
              ) : (
                <Button variant="destructive" onClick={handleDisableBtg}>
                  <Ban className="size-4" /> Desabilitar
                </Button>
              )}
            </div>
          </DetailSection>

          <Dialog open={showCredentialsForm} onOpenChange={(open) => { if (!open && !loading) setShowCredentialsForm(false) }}>
            <DialogContent size="lg" fullScreenOnMobile className="overflow-y-auto sm:max-h-[90vh]">
              <DialogHeader>
                <DialogTitle>Habilitar Integração BTG</DialogTitle>
              </DialogHeader>

              <form id="btg-credentials-form" onSubmit={handleEnableBtg} className="flex min-w-0 flex-col gap-4">
                <FormField label="Tipo de Documento *" htmlFor="btg-document-type">
                  <NativeSelect
                    id="btg-document-type"
                    value={credentials.documentType}
                    onChange={(e) => setCredentials({...credentials, documentType: e.target.value as 'CPF' | 'CNPJ'})}
                    required
                  >
                    <option value="CNPJ">CNPJ</option>
                    <option value="CPF">CPF</option>
                  </NativeSelect>
                </FormField>

                <FormField label="Número do Documento *" htmlFor="btg-document-number">
                  <Input
                    id="btg-document-number"
                    type="text"
                    value={credentials.documentNumber}
                    onChange={(e) => setCredentials({...credentials, documentNumber: e.target.value})}
                    placeholder="00.000.000/0000-00"
                    required
                  />
                </FormField>

                {credentials.documentType === 'CNPJ' && (
                  <FormField label="Razão Social" htmlFor="btg-company-name">
                    <Input
                      id="btg-company-name"
                      type="text"
                      value={credentials.companyName}
                      onChange={(e) => setCredentials({...credentials, companyName: e.target.value})}
                    />
                  </FormField>
                )}

                <FormField label="API Key *" htmlFor="btg-api-key">
                  <Input
                    id="btg-api-key"
                    type="password"
                    value={credentials.apiKey}
                    onChange={(e) => setCredentials({...credentials, apiKey: e.target.value})}
                    required
                  />
                </FormField>

                <FormField label="Client ID *" htmlFor="btg-client-id">
                  <Input
                    id="btg-client-id"
                    type="text"
                    value={credentials.clientId}
                    onChange={(e) => setCredentials({...credentials, clientId: e.target.value})}
                    required
                  />
                </FormField>

                <FormField label="Client Secret *" htmlFor="btg-client-secret">
                  <Input
                    id="btg-client-secret"
                    type="password"
                    value={credentials.clientSecret}
                    onChange={(e) => setCredentials({...credentials, clientSecret: e.target.value})}
                    required
                  />
                </FormField>
              </form>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCredentialsForm(false)}>Cancelar</Button>
                <Button type="submit" form="btg-credentials-form" disabled={loading}>
                  <Check className="size-4" />
                  {loading ? 'Habilitando...' : 'Habilitar BTG'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {selectedAccountData.isBtgEnabled && config && (
            <form onSubmit={handleUpdateConfig} className="min-w-0">
              <FormSection title="Configuração de Boletos" icon={Settings}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <FormField label="Multa Padrão (%)" htmlFor="btg-fine">
                    <Input
                      id="btg-fine"
                      type="number"
                      value={config.defaultFinePercentage}
                      onChange={(e) => setConfig({...config, defaultFinePercentage: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </FormField>

                  <FormField label="Juros Mensais (%)" htmlFor="btg-interest">
                    <Input
                      id="btg-interest"
                      type="number"
                      value={config.defaultInterestPercentage}
                      onChange={(e) => setConfig({...config, defaultInterestPercentage: parseFloat(e.target.value)})}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </FormField>

                  <FormField label="Dias para Vencimento" htmlFor="btg-days-to-due">
                    <Input
                      id="btg-days-to-due"
                      type="number"
                      value={config.defaultDaysToDue}
                      onChange={(e) => setConfig({...config, defaultDaysToDue: parseInt(e.target.value)})}
                      min="1"
                      max="365"
                    />
                  </FormField>
                </div>

                <FormField label="Instruções Padrão" htmlFor="btg-instructions">
                  <Textarea
                    id="btg-instructions"
                    value={config.defaultInstructions}
                    onChange={(e) => setConfig({...config, defaultInstructions: e.target.value})}
                    rows={3}
                    placeholder="Ex: Não receber após o vencimento"
                  />
                </FormField>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      id="btg-send-email"
                      type="checkbox"
                      checked={config.sendEmailOnIssue}
                      onChange={(e) => setConfig({...config, sendEmailOnIssue: e.target.checked})}
                      className="size-4 shrink-0 rounded-sm border-input accent-primary"
                    />
                    <Label htmlFor="btg-send-email" className="font-normal">Enviar por e-mail automaticamente ao emitir</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="btg-send-whatsapp"
                      type="checkbox"
                      checked={config.sendWhatsappOnIssue}
                      onChange={(e) => setConfig({...config, sendWhatsappOnIssue: e.target.checked})}
                      className="size-4 shrink-0 rounded-sm border-input accent-primary"
                    />
                    <Label htmlFor="btg-send-whatsapp" className="font-normal">Enviar por WhatsApp automaticamente ao emitir</Label>
                  </div>
                </div>

                <FormActions>
                  <Button type="submit" disabled={loading}>
                    <Save className="size-4" />
                    {loading ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                </FormActions>
              </FormSection>
            </form>
          )}
        </>
      )}
    </div>
  );
}
